import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import {
  RequireAuth,
  RoleAreaGuard,
  ClientOnlyGuard,
  AutoWalletGuard,
} from "./utils/ProtectedRoute";

import Login from "./pages/Login";
import Register from "./pages/Register";
import AutoWalletDeposit from "./modules/client/auto-wallet/AutoWalletDeposit";
import AutoWalletWithdrawal from "./modules/client/auto-wallet/AutoWalletWithdrawal";
import AutoWalletMyDeposits from "./modules/client/auto-wallet/AutoWalletMyDeposits";
import AutoWalletMyWithdrawals from "./modules/client/auto-wallet/AutoWalletMyWithdrawals";
import Home from "./modules/client/Home.jsx";
import AccountStatement from "./modules/client/accounts/AccountStatement.jsx";
import ProfitLoss from "./modules/client/accounts/ProfitLoss.jsx";
import Profile from "./modules/client/profile/Profile.jsx";
import CurrentPosition from "./modules/client/current_position/current-position.jsx";
import Results from "./modules/client/bet-history/Results.jsx";
import BetHistory from "./modules/client/bet-history/BetHistory.jsx";
import FullMarkets from "./modules/client/markets/full-markets.jsx";
import AgentDashboard from "./modules/agent/Dashjboard.jsx";
import AgentProfile from "./modules/agent/profile/Profile.jsx";
import BookDetails from "./modules/agent/reports/book-details.jsx";
import BookDetails2 from "./modules/agent/reports/book-details2.jsx";
import DailyPlByGameType from "./modules/agent/reports/daily-pl-by-game-type.jsx";
import DailyPlByUsers from "./modules/agent/reports/daily-pl-by-users.jsx";
import DailyPL from "./modules/agent/reports/daily-pl.jsx";
import DailyReports from "./modules/agent/reports/daily-reports.jsx";
import AccountList from "./modules/agent/users/accounts-list.jsx";
import SetPaymentDetails from "./modules/agent/users/set-payment-details.jsx";
import WhatsappSupport from "./modules/agent/users/whatsapp-support.jsx";
import DepositRequest from "./modules/agent/users/deposit-request.jsx";
import WithdrawalRequestPage from "./modules/agent/users/withdrawal-request.jsx";
import AddAccount from "./modules/agent/users/add-account.jsx";
import ManageDistributors from "./modules/agent/users/manage-distributors.jsx";
import BetIdWiseDetails from "./modules/agent/markets/BetIdWiseDetails.jsx";
import HrGrUserBook from "./modules/agent/markets/HrGrUserBook.jsx";

import FinalSheet from "./modules/agent/finalsheet/final-sheet.jsx";
import CompareSoono from "./modules/agent/finalsheet/compare-soono.jsx";
import Commission from "./modules/agent/commission/commission.jsx";
import MarketAnalysis from "./modules/agent/markets/market-analysis.jsx";
import BetLock from "./modules/agent/bet-lock/bet-lock.jsx";
import BetDelayManager from "./modules/agent/bet-delay/bet-delay-manager.jsx";
import EditBetDelay from "./modules/agent/bet-delay/edit-betdelay.jsx";
import AgentBetfairGamesDashboard from "./modules/agent/betfair-games/betfair-dashboard.jsx";
import MarketPosition from "./modules/agent/current-position/market-position.jsx";
import AccountEdit from "./modules/agent/users/account-edit.jsx";
import SetUserLimit from "./modules/agent/users/user-set-limit.jsx";
import AccountLedger from "./modules/agent/users/account-ledger.jsx";
import AccountBets from "./modules/agent/users/account-bets.jsx";
import AccountProfitLoss from "./modules/agent/users/account-profit-loss.jsx";
import AccountCurrentPosition from "./modules/agent/users/account-current-position.jsx";
import PopUpPage from "./modules/agent/components/popup-page.jsx";
import ClientPopUpPage from "./modules/client/components/client-popup-page.jsx";
import PlByHierarchicalPopup from "./modules/agent/components/plbyhierarchical-popup-page";
import DailyPlRoundPlayersPopupPage from "./modules/agent/components/daily-pl-round-players-popup-page";
import BetDetailsPopup from "./modules/agent/components/Bet-details-popup-page.jsx";
import VirtualDetailsPopup from "./modules/agent/components/Virtual-details-popup-page.jsx";
import SettlementPopup from "./modules/agent/popup/settlement-popup.jsx";
import PlDownlinePopup from "./modules/agent/popup/pl-downline-popup.jsx";
import PlDownlineDesktopPopup from "./modules/agent/popup/pl-downline-popup-desktop.jsx";

import MarketRules from "./modules/agent/popup/market-rules.jsx";
import TermsConditions from "./modules/agent/popup/terms-conditions.jsx";

import ClientVirtualDetailsPopup from "./modules/client/components/Client-Virtual-details-popup-page.jsx";
import CreditDebitPopupDetails from "./modules/client/components/credit-debit-popup-details.jsx";
import ClientCasinoDetailsPopup from "./modules/client/components/casino-details-popup-page.jsx";
import ClientTPDetailsPopup from "./modules/client/components/Client-tp-details-popup-page.jsx";
import ClientBetDetailsPopup from "./modules/client/components/Client-Bet-details-popup-page.jsx";

import BetfairGamesDashboard from "./modules/client/betfair-games/betfair-games-dashboard";
import BetfairHold from "./modules/client/betfair-games/betfair-hold";
import BetfairTurboHold from "./modules/client/betfair-games/betfair-turbo-hold";
import BetfairBlackjack from "./modules/client/betfair-games/betfair-blackjack";
import BetfairTurboBlackjack from "./modules/client/betfair-games/betfair-turbo-blackjack";
import BetfairBaccarat from "./modules/client/betfair-games/betfair-baccarat";
import BetfairTurboBaccarat from "./modules/client/betfair-games/betfair-turbo-baccarat";
import BetfairHilo from "./modules/client/betfair-games/betfair-hilo";
import BetfairTurboHilo from "./modules/client/betfair-games/betfair-turbo-hilo";
import BetfairOmaha from "./modules/client/betfair-games/betfair-omaha";
import BetfairDerby from "./modules/client/betfair-games/betfair-derby";
import BetfairTurboDerby from "./modules/client/betfair-games/betfair-turbo-derby";

import TestRecords from "./pages/TestRecords";
import DailyPlByGameTypeTest from "./pages/DailyPlByGameTypeTest";

import TPHome from "./modules/client/tp-studio/tp-home.jsx";

import GalaxyHome from "./modules/client/galaxy-casino/galaxy-home.jsx";
import Lucky7Galaxy from "./modules/client/galaxy-casino/lucky7-galaxy.jsx";
import TP20Galaxy from "./modules/client/galaxy-casino/tp20-galaxy.jsx";
import HiLowGalaxy from "./modules/client/galaxy-casino/hilo-galaxy.jsx";
import AndarBaharGalaxy from "./modules/client/galaxy-casino/andarbahar-galaxy.jsx";
import DraghonTigerGalaxy from "./modules/client/galaxy-casino/dt-galaxy.jsx";
import LuckyJetGalaxy from "./modules/client/galaxy-casino/luckyjet-galaxy.jsx";

//import Roulette from "./modules/client/tp-studio/roulette.jsx";
//import AndarBaharVirtual from "./modules/client/tp-studio/AndarBaharVirtual.jsx";
import Roulette from "./modules/client/tp-studio/roulette.jsx";
import AndarBaharVirtual from "./modules/client/tp-studio/AndarBaharVirtual.jsx";
import Aviator from "./modules/client/tp-studio/Aviator.jsx";
import AviatorX from "./modules/client/tp-studio/AviatorX.jsx";
import BlastOff from "./modules/client/tp-studio/BlastOff.jsx";
import DreamCatcher from "./modules/client/tp-studio/DreamCatcher.jsx";
import HiLo from "./modules/client/tp-studio/HiLo.jsx";
import HeadTails from "./modules/client/tp-studio/HeadTails.jsx";
import CricketX from "./modules/client/tp-studio/CricketX.jsx";
import Poker from "./modules/client/tp-studio/Poker.jsx";
import RockPaperScissor from "./modules/client/tp-studio/RockPaperScissor.jsx";
import Sicbo from "./modules/client/tp-studio/Sicbo.jsx";
import Teenpattit20 from "./modules/client/tp-studio/Teenpattit20.jsx";
import TeenpattiMuflis from "./modules/client/tp-studio/TeenpattiMuflis.jsx";
import DTVirtual from "./modules/client/tp-studio/DTVirtual.jsx";
import TeenpattiODI from "./modules/client/tp-studio/TeenpattiODI.jsx";
import AAAVirtual from "./modules/client/tp-studio/AAAVirtual.jsx";
import BollywoodVirtual from "./modules/client/tp-studio/BollywoodVirtual.jsx";
import Lucky7Virtual from "./modules/client/tp-studio/Lucky7Virtual.jsx";
import ColorGame from "./modules/client/tp-studio/ColorGame.jsx";
import RollDice from "./modules/client/tp-studio/RollDice.jsx";
import Card32Virtual from "./modules/client/tp-studio/32card.jsx";
import HighCardVirtual from "./modules/client/tp-studio/HighCard.jsx";
import DusDumVirtual from "./modules/client/tp-studio/DusDum.jsx";
import CasinoWar from "./modules/client/tp-studio/CasinoWar.jsx";
import Race20 from "./modules/client/tp-studio/Race20.jsx";
import Race17 from "./modules/client/tp-studio/Race17.jsx";
import Lucky7SecoundVirtual from "./modules/client/tp-studio/Lucky7Secound.jsx";
import CenterCardVirtual from "./modules/client/tp-studio/CenterCard.jsx";
import Lottery from "./modules/client/tp-studio/lottery.jsx";
import QueenRace from "./modules/client/tp-studio/QueenRace.jsx";
import OneCardMeter from "./modules/client/tp-studio/OneCardMeter.jsx";
import OneCard2020 from "./modules/client/tp-studio/OneCard2020.jsx";
import Trap from "./modules/client/tp-studio/Trap.jsx";
import Trio from "./modules/client/tp-studio/Trio.jsx";
import SuperOverT20 from "./modules/client/tp-studio/SuperOverT20.jsx";
import SuperOver1day from "./modules/client/tp-studio/SuperOver1day.jsx";
import ChickenRoad from "./modules/client/tp-studio/ChickenRoad.jsx";
import RockPapper from "./modules/client/tp-studio/RockPaperScissor.jsx";
import FortuneGems from "./modules/client/tp-studio/FortuneGems.jsx";
import SideBetCity from "./modules/client/tp-studio/SideBetCity.jsx";

import RoyalStarDasboard from "./modules/client/third-party-casino/royal-star-dashboard.jsx";
import RoyalStar from "./modules/client/third-party-casino/royal-star.jsx";

import WorldCasino from "./modules/client/third-party-casino/world-casino.jsx";
import RoyalCasino from "./modules/client/third-party-casino/royal-casino.jsx";
import MultiCasino from "./modules/client/third-party-casino/multi-casino.jsx";
import MultiCasinoOpen from "./modules/client/third-party-casino/multi-casino-open.jsx";
import SportsBook from "./modules/client/third-party-casino/sports-book.jsx";

/* agent betfair games */

import AgentBetfairTurboHold from "./modules/agent/betfair-games/turbo-hold.jsx";
import AgentBetfairHold from "./modules/agent/betfair-games/hold.jsx";
import AgentBetfairBlackjack from "./modules/agent/betfair-games/blackjack.jsx";
import AgentBetfairTurboBlackjack from "./modules/agent/betfair-games/turbo-blackjack.jsx";
import AgentBetfairBaccarat from "./modules/agent/betfair-games/baccarat.jsx";
import AgentBetfairTurboBaccarat from "./modules/agent/betfair-games/turbo-baccarat.jsx";
import AgentBetfairHiLo from "./modules/agent/betfair-games/hilo.jsx";
import AgentBetfairTurboHiLo from "./modules/agent/betfair-games/turbo-hilo.jsx";
import AgentBetfairOmaha from "./modules/agent/betfair-games/omaha-hi.jsx";
import AgentBetfairDerby from "./modules/agent/betfair-games/derby-race.jsx";
import AgentBetfairTurboDerby from "./modules/agent/betfair-games/turbo-derby-race.jsx";

const queryClient = new QueryClient();

function LoginRedirectIfAuth({ children }) {
  /*
  const [redirectTo, setRedirectTo] = useState(null);
  const [checked, setChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    console.log("LoginRedirectIfAuth useEffect");
    let active = true;

    (async () => {
      try {
        const res = await axiosInstance.get("/user"); // cookie-based session
        console.log("LoginRedirectIfAuth /user response:", res);
        console.log("location.pathname:", location.pathname);
        if (res.status === 200 && res.data) {
          const target =
            res.data.user_role !== 8 ? "/agent/dashboard" : "/home";
            
          if (location.pathname == "/") {
            console.log("At login page, redirecting to:", target);
            return <Navigate to={target} replace />;
          }
          console.log("After redirecting to:", target);
        }
      } catch (err) {
        console.error("Error checking auth in LoginRedirectIfAuth:", err);
      }
    })();

    return () => {
      active = false;
    };
  }, [location.pathname]);

  // if (!checked) return null;
  // if (redirectTo) return <Navigate to={redirectTo} replace />;
*/
  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        {/* Login page */}
        <Route
          path="/"
          element={
            <LoginRedirectIfAuth>
              <Login />
            </LoginRedirectIfAuth>
          }
        />

        {/* Public registration page — only works with ?ref= affiliate link */}
        <Route path="/register" element={<Register />} />

        {/* Authenticated routes */}
        <Route element={<RequireAuth />}>
          <Route element={<RoleAreaGuard />}>
            <Route path="/agent" element={<AgentDashboard />} />
            <Route path="/agent/dashboard" element={<AgentDashboard />} />
            <Route path="/agent/reset-password" element={<AgentProfile />} />
            <Route path="/agent/book-detail" element={<BookDetails />} />
            <Route path="/agent/book-detail2" element={<BookDetails2 />} />
            <Route
              path="/agent/daily-pl-by-game-type"
              element={<DailyPlByGameType />}
            />
            <Route
              path="/agent/daily-pl-by-users"
              element={<DailyPlByUsers />}
            />
            <Route path="/agent/daily-pl" element={<DailyPL />} />
            <Route path="/agent/daily-reports" element={<DailyReports />} />
            <Route path="/agent/final-sheet" element={<FinalSheet />} />
            <Route path="/agent/compare-soono" element={<CompareSoono />} />
            <Route path="/agent/commission" element={<Commission />} />
            <Route
              path="/agent/user-book/:cat_mid/:market_type/"
              element={<BetIdWiseDetails />}
            />
            <Route
              path="/agent/hr-gr-user-book/:cat_mid"
              element={<HrGrUserBook />}
            />
            <Route path="/agent/accounts" element={<AccountList />} />
            <Route
              path="/agent/accounts/chart/:userId?"
              element={<AccountList />}
            />
            <Route path="/agent/accounts/add" element={<AddAccount />} />
            <Route
              path="/agent/accounts/distributors"
              element={<ManageDistributors />}
            />
            <Route
              path="/agent/accounts/set-payment-details"
              element={<SetPaymentDetails />}
            />
            <Route
              path="/agent/accounts/whatsapp-support"
              element={<WhatsappSupport />}
            />
            <Route
              path="/agent/accounts/deposit-request"
              element={<DepositRequest />}
            />
            <Route
              path="/agent/accounts/withdrawal-request"
              element={<WithdrawalRequestPage />}
            />

            <Route
              path="/agent/market-analysis/:marketId"
              element={<MarketAnalysis />}
            />

            <Route
              path="/agent/market-analysis/:marketId/:marketType"
              element={<MarketAnalysis />}
            />

            <Route path="/agent/bet-lock" element={<BetLock />} />
            <Route path="/agent/bet-delay" element={<BetDelayManager />} />
            <Route path="/agent/edit-betdelay" element={<EditBetDelay />} />
            <Route
              path="/agent/betfair-dashboard"
              element={<AgentBetfairGamesDashboard />}
            />
            <Route
              path="/agent/market-analyses-list"
              element={<MarketPosition />}
            />
            <Route path="/agent/edit/:userId" element={<AccountEdit />} />
            <Route path="/agent/setuserlimit" element={<SetUserLimit />} />
            <Route path="/agent/ledger/:userId" element={<AccountLedger />} />
            <Route path="/agent/bets/:userId" element={<AccountBets />} />
            <Route
              path="/agent/profit-loss/:userId"
              element={<AccountProfitLoss />}
            />
            <Route
              path="/agent/current-position/:userId"
              element={<AccountCurrentPosition />}
            />

            <Route
              path="/agent/popup-page/:type/:userId"
              element={<PopUpPage />}
            />

            <Route path="/agent/hold" element={<AgentBetfairHold />} />
            <Route
              path="/agent/turbo-hold"
              element={<AgentBetfairTurboHold />}
            />
            <Route
              path="/agent/blackjack"
              element={<AgentBetfairBlackjack />}
            />
            <Route
              path="/agent/turbo-blackjack"
              element={<AgentBetfairTurboBlackjack />}
            />
            <Route path="/agent/baccarat" element={<AgentBetfairBaccarat />} />
            <Route
              path="/agent/turbo-baccarat"
              element={<AgentBetfairTurboBaccarat />}
            />
            <Route path="/agent/hilo" element={<AgentBetfairHiLo />} />
            <Route
              path="/agent/turbo-hilo"
              element={<AgentBetfairTurboHiLo />}
            />
            <Route path="/agent/omaha-hi" element={<AgentBetfairOmaha />} />
            <Route path="/agent/derby-race" element={<AgentBetfairDerby />} />
            <Route
              path="/agent/turbo-derby-race"
              element={<AgentBetfairTurboDerby />}
            />

            <Route path="/home" element={<Home />} />
            <Route element={<AutoWalletGuard />}>
              <Route
                path="/auto-wallet/deposit"
                element={<AutoWalletDeposit />}
              />
              <Route
                path="/auto-wallet/withdrawal"
                element={<AutoWalletWithdrawal />}
              />
              <Route
                path="/auto-wallet/my-deposits"
                element={<AutoWalletMyDeposits />}
              />
              <Route
                path="/auto-wallet/my-withdrawals"
                element={<AutoWalletMyWithdrawals />}
              />
            </Route>
            <Route
              path="/accounts/account-statement"
              element={<AccountStatement />}
            />
            <Route path="/accounts/profit-loss" element={<ProfitLoss />} />
            <Route path="/profile" element={<Profile />} />
            <Route
              path="/current-position-list"
              element={<CurrentPosition />}
            />

            <Route path="/results" element={<Results />} />
            <Route path="/bet-history" element={<BetHistory />} />
            <Route path="/betfair-studio" element={<BetfairGamesDashboard />} />
            <Route path="/betfair-hold" element={<BetfairHold />} />
            <Route path="/betfair-turbo-hold" element={<BetfairTurboHold />} />
            <Route path="/betfair-blackjack" element={<BetfairBlackjack />} />
            <Route
              path="/betfair-turbo-blackjack"
              element={<BetfairTurboBlackjack />}
            />
            <Route path="/betfair-baccarat" element={<BetfairBaccarat />} />
            <Route
              path="/betfair-turbo-baccarat"
              element={<BetfairTurboBaccarat />}
            />
            <Route path="/betfair-hilo" element={<BetfairHilo />} />
            <Route path="/betfair-turbo-hilo" element={<BetfairTurboHilo />} />
            <Route path="/betfair-omaha" element={<BetfairOmaha />} />
            <Route path="/betfair-derby" element={<BetfairDerby />} />
            <Route
              path="/betfair-turbo-derby"
              element={<BetfairTurboDerby />}
            />

            <Route path="/galaxy-casino" element={<GalaxyHome />} />
            <Route path="/galaxy-casino/:tabId" element={<GalaxyHome />} />
            <Route path="/lucky7-galaxy" element={<Lucky7Galaxy />} />
            <Route path="/tp20-galaxy" element={<TP20Galaxy />} />
            <Route path="/hilo-galaxy" element={<HiLowGalaxy />} />
            <Route path="/andarbahar-galaxy" element={<AndarBaharGalaxy />} />
            <Route path="/dt-galaxy" element={<DraghonTigerGalaxy />} />
            <Route path="/luckyjet-galaxy" element={<LuckyJetGalaxy />} />

            <Route path="/royal-star" element={<RoyalStarDasboard />} />
            <Route path="/royal-star/:casinoId" element={<RoyalStar />} />

            <Route path="/world-casino" element={<WorldCasino />} />
            <Route path="/royal-casino" element={<RoyalCasino />} />

            <Route path="/multi-casino" element={<MultiCasino />} />
            <Route path="/royal-casino/:casinoId" element={<RoyalCasino />} />
            <Route
              path="/multi-casino-open/:code/:gameCode"
              element={<MultiCasinoOpen />}
            />
            <Route path="/sports-book" element={<SportsBook />} />

            <Route path="/test-records" element={<TestRecords />} />
            <Route
              path="/test-daily-pl-by-game-type"
              element={<DailyPlByGameTypeTest />}
            />
          </Route>

          {/* TP Studio — punter (role 8) only; not subject to RoleAreaGuard */}
          <Route element={<ClientOnlyGuard />}>
            <Route path="/tp-studio" element={<TPHome />} />
            <Route path="/tp-studio/roulette" element={<Roulette />} />
            <Route
              path="/tp-studio/andarbahar-virtual"
              element={<AndarBaharVirtual />}
            />
            <Route path="/tp-studio/aviator" element={<Aviator />} />
            <Route path="/tp-studio/aviatorx" element={<AviatorX />} />
            <Route path="/tp-studio/blastoff" element={<BlastOff />} />
            <Route path="/tp-studio/dream-catcher" element={<DreamCatcher />} />
            <Route path="/tp-studio/hilo" element={<HiLo />} />
            <Route path="/tp-studio/head-tail" element={<HeadTails />} />
            <Route path="/tp-studio/cricketx" element={<CricketX />} />
            <Route path="/tp-studio/poker" element={<Poker />} />
            <Route
              path="/tp-studio/rock-paper-scissor"
              element={<RockPaperScissor />}
            />
            <Route path="/tp-studio/sicbo" element={<Sicbo />} />
            <Route path="/tp-studio/tp20-virtual" element={<Teenpattit20 />} />
            <Route
              path="/tp-studio/teenpatti-muflis"
              element={<TeenpattiMuflis />}
            />
            <Route path="/tp-studio/dt-virtual" element={<DTVirtual />} />
            <Route path="/tp-studio/teenpatti-odi" element={<TeenpattiODI />} />
            <Route path="/tp-studio/aaa-virtual" element={<AAAVirtual />} />
            <Route
              path="/tp-studio/bollywood-virtual"
              element={<BollywoodVirtual />}
            />
            <Route
              path="/tp-studio/lucky7-virtual"
              element={<Lucky7Virtual />}
            />
            <Route path="/tp-studio/color-game" element={<ColorGame />} />
            <Route path="/tp-studio/roll-dice" element={<RollDice />} />
            <Route path="/tp-studio/race20-virtual" element={<Race20 />} />
            <Route path="/tp-studio/race17-virtual" element={<Race17 />} />
            <Route
              path="/tp-studio/card32-virtual"
              element={<Card32Virtual />}
            />
            <Route
              path="/tp-studio/highcard-virtual"
              element={<HighCardVirtual />}
            />
            <Route
              path="/tp-studio/duskadum-virtual"
              element={<DusDumVirtual />}
            />
            <Route
              path="/tp-studio/casinowar-virtual"
              element={<CasinoWar />}
            />
            <Route
              path="/tp-studio/lucky7second-virtual"
              element={<Lucky7SecoundVirtual />}
            />
            <Route
              path="/tp-studio/centercard-virtual"
              element={<CenterCardVirtual />}
            />
            <Route path="/tp-studio/lottery" element={<Lottery />} />
            <Route path="/tp-studio/queenrace" element={<QueenRace />} />
            <Route
              path="/tp-studio/one-card-meter"
              element={<OneCardMeter />}
            />
            <Route path="/tp-studio/one-card2020" element={<OneCard2020 />} />
            <Route path="/tp-studio/trap" element={<Trap />} />
            <Route path="/tp-studio/trio" element={<Trio />} />
            <Route path="/tp-studio/superovert20" element={<SuperOverT20 />} />
            <Route
              path="/tp-studio/superover1day"
              element={<SuperOver1day />}
            />
            <Route path="/tp-studio/chickenroad" element={<ChickenRoad />} />
            <Route
              path="/tp-studio/rock-paper-scissors"
              element={<RockPapper />}
            />
            <Route path="/tp-studio/fortunegems" element={<FortuneGems />} />
            <Route path="/tp-studio/side-bet-city" element={<SideBetCity />} />
          </Route>

          {/* Shared routes — any authenticated role */}
          <Route path="/market/:marketId" element={<FullMarkets />} />
          <Route path="/trans-page/:transid" element={<ClientPopUpPage />} />
          <Route path="/transDetails/:transid" element={<ClientPopUpPage />} />
          <Route
            path="/plby-hierarchical/:transid"
            element={<PlByHierarchicalPopup />}
          />
          <Route
            path="/daily-pl-round-players/:transid"
            element={<DailyPlRoundPlayersPopupPage />}
          />
          <Route
            path="/bet-details-view/:transid"
            element={<BetDetailsPopup />}
          />
          <Route
            path="/virtual-details-view/:transid"
            element={<VirtualDetailsPopup />}
          />

          <Route
            path="/agent/settlement/:userId"
            element={<SettlementPopup />}
          />

          <Route
            path="/clinet-bet-details-view/:transid"
            element={<ClientBetDetailsPopup />}
          />
          <Route
            path="/client-virtual-details-view/:transid"
            element={<ClientVirtualDetailsPopup />}
          />
          <Route
            path="/credit-debit-popup-details/:transid"
            element={<CreditDebitPopupDetails />}
          />
          <Route
            path="/client-tp-details-view/:transid"
            element={<ClientTPDetailsPopup />}
          />
          <Route
            path="/pl-downline-view/:userId"
            element={<PlDownlinePopup />}
          />
          <Route
            path="/pl-downline-des/:userId"
            element={<PlDownlineDesktopPopup />}
          />
          <Route
            path="/client-casino-details-view/:transid"
            element={<ClientCasinoDetailsPopup />}
          />
        </Route>
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
